/**
 * @fileoverview Sentry/GlitchTip Configuration
 *
 * Centralizes error tracking configuration for external monitoring services.
 * Uses GlitchTip (Sentry-compatible) for error tracking and performance monitoring.
 *
 * Environment Variables:
 * - VITE_SENTRY_DSN: Data Source Name for GlitchTip instance
 * - VITE_SENTRY_ENVIRONMENT: Environment name (development, staging, production)
 * - VITE_APP_VERSION: Application version for release tracking
 *
 * @author Engineering Team
 * @copyright Cirrus Invicta 2025
 * @private
 */

import * as Sentry from '@sentry/vue'
import { logger } from '@/utils/logger'
import packageJson from '../../package.json'

/**
 * Initialize Sentry/GlitchTip error tracking
 *
 * @param {Object} app - Vue application instance
 * @returns {boolean} - Whether Sentry was initialized successfully
 */
export function initializeSentry(app) {
    const dsn = import.meta.env.VITE_SENTRY_DSN
    const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE
    const release = packageJson.version || 'unknown'

    // Don't initialize if no DSN is configured
    if (!dsn) {
        logger.info('Sentry/GlitchTip not configured - skipping initialization')
        return false
    }

    try {
        Sentry.init({
            app,
            dsn,
            environment,
            release: `lantern-frontend@${release}`,

            // Enable debug mode in development only
            debug: environment === 'development',

            // Integrations
            integrations: [
                // Simplified browser tracing without router instrumentation
                // GlitchTip may not support all Sentry features
                Sentry.browserTracingIntegration()
            ],

            // Performance Monitoring
            tracesSampleRate: environment === 'production' ? 0.01 : 1.0, // 100% in dev for testing

            // Session Replay (optional - requires replay integration)
            // replaysSessionSampleRate: 0.1, // 10% of sessions
            // replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

            // Vue-specific options
            attachProps: true, // Include component props in error reports
            logErrors: true, // Call Vue's original logError function

            // Additional configuration
            beforeSend(event, hint) {
                // Filter out errors we don't want to track
                const error = hint.originalException

                // Don't send network errors to Sentry (already logged locally)
                if (error?.name === 'AxiosError' && error?.config?.url) {
                    logger.debug('Filtering axios error from Sentry', {
                        url: error.config.url,
                        status: error.response?.status
                    })
                    return null
                }

                // Add custom context
                event.tags = {
                    ...event.tags,
                    vue_version: app.version
                }

                return event
            },

            // Ignore specific errors
            ignoreErrors: [
                // Browser extensions
                'top.GLOBALS',
                'ChunkLoadError', // Vite hot reload errors
                // Network errors (handled by our error handler)
                'Network Error',
                'NetworkError'
            ]
        })

        logger.info('Sentry/GlitchTip initialized', {
            environment,
            release,
            dsn: dsn.substring(0, 30) + '...' // Log partial DSN for verification
        })

        return true
    } catch (error) {
        logger.error('Failed to initialize Sentry/GlitchTip', { error: error.message })
        return false
    }
}

/**
 * Manually capture an exception to Sentry
 *
 * @param {Error} error - Error object to capture
 * @param {Object} context - Additional context for the error
 */
export function captureException(error, context = {}) {
    Sentry.captureException(error, {
        contexts: {
            custom: context
        }
    })
}

/**
 * Set user context for error tracking
 *
 * @param {Object} user - User information
 * @param {string} user.id - User ID
 * @param {string} user.email - User email
 * @param {string} user.username - Username
 */
export function setUserContext(user) {
    if (user) {
        Sentry.setUser({
            id: user.id,
            email: user.email,
            username: user.username
        })
    } else {
        Sentry.setUser(null) // Clear user context on logout
    }
}

/**
 * Add breadcrumb for debugging
 *
 * @param {string} message - Breadcrumb message
 * @param {string} category - Breadcrumb category
 * @param {Object} data - Additional data
 */
export function addBreadcrumb(message, category = 'custom', data = {}) {
    Sentry.addBreadcrumb({
        message,
        category,
        data,
        level: 'info'
    })
}
